import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, FolderTree, History, Loader2, RefreshCw, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { ScrollArea } from 'components/ui/scroll-area';
import { Separator } from 'components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useConfig } from 'hooks/use-config';
import { useDebounce } from 'hooks/use-debounce';
import { useToast } from 'hooks/use-toast';
import {
  browseIntegrationConnection,
  clearIntegrationSelections,
  getIntegrationSyncJob,
  IntegrationIndexedResource,
  getIntegrationSelections,
  IntegrationBrowseNode,
  IntegrationSelectionNode,
  IntegrationSyncJob,
  listIntegrationIndexedResources,
  listIntegrationSyncJobs,
  saveIntegrationSelections,
  startIntegrationSyncJob,
} from 'services/integration-service';

interface IntegrationBrowserDialogProps {
  connectionId: string | null;
  onOpenChange: (open: boolean) => void;
  onSyncStarted?: () => void;
  open: boolean;
  providerId: string;
  providerName: string;
}

const toSelectionNode = (node: IntegrationBrowseNode): IntegrationSelectionNode => ({
  source_node_id: node.source_node_id,
  node_type: node.node_type,
  node_title: node.title,
});

const getSyncJobStatusTone = (status?: string) => {
  switch (status) {
    case 'succeeded':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
    case 'failed':
      return 'bg-destructive/10 text-destructive';
    case 'running':
    case 'queued':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const formatTimestamp = (value?: string | null, fallback = 'Not available') => {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const BROWSE_PAGE_SIZE = 6;
const INDEXED_RESOURCES_PAGE_SIZE = 10;
const SELECTED_PAGE_SIZE = 10;

const matchesSearch = (values: Array<string | null | undefined>, query: string) => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return true;
  }

  return values.some((value) => value?.toLowerCase().includes(normalizedQuery));
};

const clampPage = (page: number, totalItems: number, pageSize: number) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  return Math.min(Math.max(page, 1), totalPages);
};

const formatSelectionState = (value?: string | null) => {
  if (!value) {
    return 'Unknown';
  }

  return value
    .split(/[_\s]+/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const getSelectionStateBadgeClassName = (value?: string | null) => {
  const normalizedValue = value?.trim().toLowerCase();

  if (normalizedValue === 'inherited') {
    return 'border-transparent bg-emerald-100 text-emerald-900 transition-none dark:bg-emerald-950/50 dark:text-emerald-200';
  }

  return 'border-transparent bg-secondary text-secondary-foreground transition-none';
};

const normalizeProviderId = (value?: string) =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-');

const isGoogleDriveProvider = (providerId: string) => normalizeProviderId(providerId) === 'google-drive';
const isSharePointProvider = (providerId: string) => {
  const normalized = normalizeProviderId(providerId);
  return normalized === 'microsoft-sharepoint' || normalized === 'sharepoint';
};

const isGoogleDriveRootNode = (node?: IntegrationBrowseNode) =>
  Boolean(
    node &&
    node.has_children &&
    node.title.trim().toLowerCase() === 'my drive' &&
    node.node_type.trim().toLowerCase() === 'drive'
  );

const filterRootNodes = (nodes: IntegrationBrowseNode[], providerId: string) => {
  if (!isSharePointProvider(providerId)) {
    return nodes;
  }

  return nodes.filter((node) => !isGoogleDriveRootNode(node));
};

export function IntegrationBrowserDialog({
  connectionId,
  onOpenChange,
  onSyncStarted,
  open,
  providerId,
  providerName,
}: IntegrationBrowserDialogProps) {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const notAvailableLabel = t('NOT_AVAILABLE') || 'Not available';
  const [currentNodes, setCurrentNodes] = useState<IntegrationBrowseNode[]>([]);
  const [path, setPath] = useState<IntegrationBrowseNode[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<Record<string, IntegrationSelectionNode>>({});
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [isLoadingSelections, setIsLoadingSelections] = useState(false);
  const [isLoadingSyncJobs, setIsLoadingSyncJobs] = useState(false);
  const [isLoadingSyncJobDetail, setIsLoadingSyncJobDetail] = useState(false);
  const [isLoadingIndexedResources, setIsLoadingIndexedResources] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'browse' | 'resources' | 'logs'>('browse');
  const [syncJobs, setSyncJobs] = useState<IntegrationSyncJob[]>([]);
  const [selectedSyncJobId, setSelectedSyncJobId] = useState<string | null>(null);
  const [selectedSyncJob, setSelectedSyncJob] = useState<IntegrationSyncJob | null>(null);
  const [indexedResources, setIndexedResources] = useState<IntegrationIndexedResource[]>([]);
  const [indexedResourcesTotal, setIndexedResourcesTotal] = useState(0);
  const [indexedResourcesOffset, setIndexedResourcesOffset] = useState(0);
  const [indexedResourcesSearchQuery, setIndexedResourcesSearchQuery] = useState('');
  const [indexedResourcesSubmittedQuery, setIndexedResourcesSubmittedQuery] = useState('');
  const [focusedSelectedNodeId, setFocusedSelectedNodeId] = useState<string | null>(null);
  const [browseSearchQuery, setBrowseSearchQuery] = useState('');
  const [browsePage, setBrowsePage] = useState(1);
  const [selectedSearchQuery, setSelectedSearchQuery] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const nodeRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const selectedSyncJobIdRef = useRef<string | null>(null);
  const hasLoadedSyncJobsRef = useRef(false);
  const syncJobsRequestIdRef = useRef(0);
  const syncJobDetailRequestIdRef = useRef(0);
  const indexedResourcesRequestIdRef = useRef(0);
  const googleDriveBrowseEnabled = isGoogleDriveProvider(providerId);
  const debouncedIndexedResourcesSearchQuery = useDebounce(indexedResourcesSearchQuery, 400);

  useEffect(() => {
    selectedSyncJobIdRef.current = selectedSyncJobId;
  }, [selectedSyncJobId]);

  useEffect(() => {
    setBrowseSearchQuery('');
    setBrowsePage(1);
  }, [path.length, open]);

  useEffect(() => {
    setBrowsePage((current) => clampPage(current, currentNodes.length, BROWSE_PAGE_SIZE));
  }, [currentNodes.length]);

  useEffect(() => {
    setSelectedPage(1);
  }, [selectedSearchQuery]);

  useEffect(() => {
    syncJobsRequestIdRef.current += 1;
    syncJobDetailRequestIdRef.current += 1;

    if (!open) {
      return;
    }

    setCurrentNodes([]);
    setPath([]);
    setFocusedSelectedNodeId(null);
    setBrowseSearchQuery('');
    setBrowsePage(1);
    setSelectedSearchQuery('');
    setSelectedPage(1);
    setSyncJobs([]);
    setSelectedSyncJob(null);
    setSelectedSyncJobId(null);
    setIndexedResources([]);
    setIndexedResourcesTotal(0);
    setIndexedResourcesOffset(0);
    setIndexedResourcesSearchQuery('');
    setIndexedResourcesSubmittedQuery('');
    hasLoadedSyncJobsRef.current = false;
  }, [connectionId, open, providerId]);

  const loadIndexedResources = useCallback(async (nextOffset = 0, rawQuery = indexedResourcesSearchQuery) => {
    if (!configurationSettings || !connectionId) {
      return;
    }

    const requestId = ++indexedResourcesRequestIdRef.current;
    const trimmedQuery = rawQuery.trim();
    const appliedQuery = trimmedQuery.length >= 2 ? trimmedQuery : undefined;
    setIsLoadingIndexedResources(true);

    try {
      const response = await listIntegrationIndexedResources(configurationSettings, connectionId, {
        limit: INDEXED_RESOURCES_PAGE_SIZE,
        offset: nextOffset,
        name_query: appliedQuery,
      });

      if (requestId !== indexedResourcesRequestIdRef.current) {
        return;
      }

      setIndexedResources(response.items || []);
      setIndexedResourcesTotal(response.total || 0);
      setIndexedResourcesOffset(response.offset || 0);
      setIndexedResourcesSubmittedQuery(appliedQuery || '');
    } catch (error) {
      if (requestId !== indexedResourcesRequestIdRef.current) {
        return;
      }

      toast({
        title: t('INDEXED_RESOURCES_UNAVAILABLE') || 'Indexed resources unavailable',
        description: error instanceof Error ? error.message : t('FAILED_TO_LOAD_INDEXED_RESOURCES') || 'Failed to load indexed resources',
        variant: 'destructive',
      });
    } finally {
      if (requestId === indexedResourcesRequestIdRef.current) {
        setIsLoadingIndexedResources(false);
      }
    }
  }, [configurationSettings, connectionId, indexedResourcesSearchQuery, t, toast]);

  const loadSyncJobDetail = async (syncJobId: string) => {
    if (!configurationSettings) {
      return;
    }

    const requestId = ++syncJobDetailRequestIdRef.current;
    setIsLoadingSyncJobDetail(true);

    try {
      const response = await getIntegrationSyncJob(configurationSettings, syncJobId);
      if (requestId !== syncJobDetailRequestIdRef.current) {
        return;
      }
      setSelectedSyncJob(response);
      setSelectedSyncJobId(response.id);
      setSyncJobs((prev) =>
        prev.map((job) => (job.id === response.id ? response : job))
      );
    } catch (error) {
      if (requestId !== syncJobDetailRequestIdRef.current) {
        return;
      }
      toast({
        title: t('SYNC_ACTIVITY_UNAVAILABLE') || 'Sync activity unavailable',
        description: error instanceof Error ? error.message : t('FAILED_TO_LOAD_SYNC_JOB_DETAILS') || 'Failed to load sync job details',
        variant: 'destructive',
      });
    } finally {
      if (requestId === syncJobDetailRequestIdRef.current) {
        setIsLoadingSyncJobDetail(false);
      }
    }
  };

  const loadSyncJobs = async (preferredSyncJobId?: string) => {
    if (!configurationSettings || !connectionId) {
      return;
    }

    const requestId = ++syncJobsRequestIdRef.current;
    setIsLoadingSyncJobs(true);

    try {
      const response = await listIntegrationSyncJobs(configurationSettings, connectionId);
      if (requestId !== syncJobsRequestIdRef.current) {
        return;
      }

      const jobs = response || [];
      setSyncJobs(jobs);

      const nextSyncJobId = [preferredSyncJobId, selectedSyncJobIdRef.current, jobs[0]?.id].find(
        (jobId) => jobId && jobs.some((job) => job.id === jobId)
      );

      if (nextSyncJobId) {
        await loadSyncJobDetail(nextSyncJobId);
      } else {
        setSelectedSyncJob(null);
        setSelectedSyncJobId(null);
      }
    } catch (error) {
      if (requestId !== syncJobsRequestIdRef.current) {
        return;
      }
      toast({
        title: t('SYNC_ACTIVITY_UNAVAILABLE') || 'Sync activity unavailable',
        description: error instanceof Error ? error.message : t('FAILED_TO_LOAD_SYNC_JOBS') || 'Failed to load sync jobs',
        variant: 'destructive',
      });
    } finally {
      if (requestId === syncJobsRequestIdRef.current) {
        setIsLoadingSyncJobs(false);
      }
    }
  };

  const handleTabChange = (value: 'browse' | 'resources' | 'logs') => {
    setActiveTab(value);

    if (value !== 'logs' || !open || !connectionId || !configurationSettings || hasLoadedSyncJobsRef.current) {
      return;
    }

    hasLoadedSyncJobsRef.current = true;
    void loadSyncJobs();
  };

  const loadNodes = async (parentNodeId?: string) => {
    if (!configurationSettings || !connectionId) {
      return;
    }

    setIsLoadingTree(true);

    try {
      const response = await browseIntegrationConnection(configurationSettings, connectionId, parentNodeId);
      const nextNodes = response.nodes || [];
      setCurrentNodes(parentNodeId ? nextNodes : filterRootNodes(nextNodes, providerId));
    } finally {
      setIsLoadingTree(false);
    }
  };

  useEffect(() => {
    if (!open || !connectionId || !configurationSettings) {
      return;
    }

    let isCancelled = false;

    const loadDialogData = async () => {
      indexedResourcesRequestIdRef.current += 1;
      setIsLoadingTree(true);
      setIsLoadingSelections(true);
      setActiveTab('browse');

      try {
        const [browseResponse, selectionResponse] = await Promise.all([
          browseIntegrationConnection(configurationSettings, connectionId),
          getIntegrationSelections(configurationSettings, connectionId),
        ]);

        if (isCancelled) {
          return;
        }

        const rootNodes = filterRootNodes(browseResponse.nodes || [], providerId);
        const googleDriveRootNode =
          googleDriveBrowseEnabled && rootNodes.length === 1 && isGoogleDriveRootNode(rootNodes[0])
            ? rootNodes[0]
            : null;

        let nextPath: IntegrationBrowseNode[] = [];
        let nextNodes = rootNodes;

        if (googleDriveRootNode) {
          try {
            const childBrowseResponse = await browseIntegrationConnection(
              configurationSettings,
              connectionId,
              googleDriveRootNode.source_node_id
            );

            if (isCancelled) {
              return;
            }

            nextPath = [googleDriveRootNode];
            nextNodes = childBrowseResponse.nodes || [];
          } catch {
            nextPath = [];
            nextNodes = rootNodes;
          }
        }

        setPath(nextPath);
        setCurrentNodes(nextNodes);
        setFocusedSelectedNodeId(null);
        setBrowseSearchQuery('');
        setBrowsePage(1);
        setSelectedSearchQuery('');
        setSelectedPage(1);
        setSelectedNodes(
          (selectionResponse || []).reduce<Record<string, IntegrationSelectionNode>>((acc, node) => {
            acc[node.source_node_id] = node;
            return acc;
          }, {})
        );
      } catch (error) {
        if (!isCancelled) {
          toast({
            title: t('BROWSE_UNAVAILABLE') || 'Browse unavailable',
            description: error instanceof Error
              ? error.message
              : t('FAILED_TO_LOAD_PROVIDER_RESOURCES', { name: providerName }) || `Failed to load ${providerName} resources`,
            variant: 'destructive',
          });
          onOpenChange(false);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingTree(false);
          setIsLoadingSelections(false);
        }
      }
    };

    void loadDialogData();

    return () => {
      isCancelled = true;
    };
  }, [configurationSettings, connectionId, googleDriveBrowseEnabled, onOpenChange, open, providerId, providerName, t, toast]);

  useEffect(() => {
    if (!open || !connectionId || activeTab !== 'resources') {
      return;
    }

    void loadIndexedResources(0, debouncedIndexedResourcesSearchQuery);
  }, [activeTab, connectionId, debouncedIndexedResourcesSearchQuery, loadIndexedResources, open]);

  const handleToggleSelection = (node: IntegrationBrowseNode, checked: boolean) => {
    setSelectedNodes((current) => {
      const next = { ...current };

      if (checked) {
        next[node.source_node_id] = toSelectionNode(node);
      } else {
        delete next[node.source_node_id];
      }

      return next;
    });
  };

  const handleOpenNode = async (node: IntegrationBrowseNode) => {
    if (!node.has_children) {
      return;
    }

    try {
      setPath((current) => [...current, node]);
      await loadNodes(node.source_node_id);
    } catch (error) {
      setPath((current) => current.slice(0, -1));
      toast({
        title: t('BROWSE_UNAVAILABLE') || 'Browse unavailable',
        description: error instanceof Error ? error.message : t('FAILED_TO_LOAD_CHILD_RESOURCES') || 'Failed to load child resources',
        variant: 'destructive',
      });
    }
  };

  const handleGoBack = async () => {
    const nextPath = path.slice(0, -1);
    const parentNode = nextPath.at(-1);

    setPath(nextPath);

    try {
      await loadNodes(parentNode?.source_node_id);
    } catch (error) {
      toast({
        title: t('BROWSE_UNAVAILABLE') || 'Browse unavailable',
        description: error instanceof Error ? error.message : t('FAILED_TO_RELOAD_PARENT_RESOURCES') || 'Failed to reload parent resources',
        variant: 'destructive',
      });
    }
  };

  const handleDone = async () => {
    if (!configurationSettings || !connectionId) {
      return;
    }

    setIsSubmitting(true);

    try {
      const nodes = Object.values(selectedNodes);

      if (nodes.length > 0) {
        await saveIntegrationSelections(configurationSettings, connectionId, { nodes });
      } else {
        await clearIntegrationSelections(configurationSettings, connectionId);
      }

      const syncJob = await startIntegrationSyncJob(configurationSettings, connectionId, {
        mode: 'incremental',
        trigger: 'manual',
      });

      toast({
        variant: 'info',
        title: t('SYNC_STARTED') || 'Sync started',
        description:
          nodes.length > 0
            ? t('SYNC_STARTED_FOR_SELECTED', { name: providerName, count: nodes.length }) ||
              `${providerName} sync started for ${nodes.length} selected source${nodes.length === 1 ? '' : 's'}.`
            : t('SYNC_STARTED_FOR_CONNECTION', { name: providerName }) ||
              `${providerName} sync started for the full connection.`,
      });

      setPath([]);
      setCurrentNodes([]);
      setFocusedSelectedNodeId(null);
      setSelectedSyncJob(syncJob);
      setSelectedSyncJobId(syncJob.id);
      onOpenChange(false);
      onSyncStarted?.();

      if (syncJob.status && syncJob.status !== 'running') {
        toast({
          variant: 'info',
          title: t('SYNC_QUEUED') || 'Sync queued',
          description: t('SYNC_JOB_CURRENTLY_STATUS', { id: syncJob.id, status: syncJob.status }) ||
            `Sync job ${syncJob.id} is currently ${syncJob.status}.`,
        });
      }
    } catch (error) {
      toast({
        title: t('SYNC_FAILED_TO_START') || 'Sync failed to start',
        description: error instanceof Error ? error.message : t('FAILED_TO_SAVE_SELECTIONS_AND_START_SYNC') || 'Failed to save selections and start sync',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCount = Object.keys(selectedNodes).length;
  const selectedNodeList = Object.values(selectedNodes);
  const isLoading = isLoadingTree || isLoadingSelections;
  const isRefreshing = activeTab === 'logs'
    ? isLoadingSyncJobs || isLoadingSyncJobDetail
    : activeTab === 'resources'
      ? isLoadingIndexedResources
      : isLoadingTree;
  const visibleNodeIds = new Set(currentNodes.map((node) => node.source_node_id));
  const filteredSelectedNodes = selectedNodeList.filter((node) =>
    matchesSearch([node.node_title, node.node_type, node.source_node_id], selectedSearchQuery)
  );
  const selectedTotalPages = Math.max(1, Math.ceil(filteredSelectedNodes.length / SELECTED_PAGE_SIZE));
  const safeSelectedPage = clampPage(selectedPage, filteredSelectedNodes.length, SELECTED_PAGE_SIZE);
  const paginatedSelectedNodes = filteredSelectedNodes.slice(
    (safeSelectedPage - 1) * SELECTED_PAGE_SIZE,
    safeSelectedPage * SELECTED_PAGE_SIZE
  );
  const filteredCurrentNodes = currentNodes.filter((node) =>
    matchesSearch([node.title, node.node_type, node.description, node.source_node_id], browseSearchQuery)
  );
  const isAtGoogleDriveRoot = googleDriveBrowseEnabled && path.length === 1 && isGoogleDriveRootNode(path[0]);
  const browseTotalPages = Math.max(1, Math.ceil(filteredCurrentNodes.length / BROWSE_PAGE_SIZE));
  const safeBrowsePage = clampPage(browsePage, filteredCurrentNodes.length, BROWSE_PAGE_SIZE);
  const browseShownCount = Math.min(safeBrowsePage * BROWSE_PAGE_SIZE, filteredCurrentNodes.length);
  const paginatedCurrentNodes = filteredCurrentNodes.slice(
    (safeBrowsePage - 1) * BROWSE_PAGE_SIZE,
    safeBrowsePage * BROWSE_PAGE_SIZE
  );
  const syncJobStats = selectedSyncJob?.stats && typeof selectedSyncJob.stats === 'object'
    ? Object.entries(selectedSyncJob.stats as Record<string, unknown>)
    : [];
  const indexedResourcesPage = Math.floor(indexedResourcesOffset / INDEXED_RESOURCES_PAGE_SIZE) + 1;
  const indexedResourcesTotalPages = Math.max(1, Math.ceil(indexedResourcesTotal / INDEXED_RESOURCES_PAGE_SIZE));
  const indexedResourcesShownFrom = indexedResourcesTotal === 0 ? 0 : indexedResourcesOffset + 1;
  const indexedResourcesShownTo = indexedResourcesTotal === 0
    ? 0
    : Math.min(indexedResourcesOffset + indexedResources.length, indexedResourcesTotal);
  const indexedResourcesSearchHintVisible =
    indexedResourcesSearchQuery.trim().length > 0 && indexedResourcesSearchQuery.trim().length < 2;

  const handleRefreshClick = async () => {
    if (activeTab === 'logs') {
      await loadSyncJobs();
      return;
    }

    if (activeTab === 'resources') {
      await loadIndexedResources(indexedResourcesOffset, indexedResourcesSubmittedQuery);
      return;
    }

    try {
      await loadNodes(path.at(-1)?.source_node_id);
    } catch (error) {
      toast({
        title: t('BROWSE_UNAVAILABLE') || 'Browse unavailable',
        description: error instanceof Error ? error.message : t('FAILED_TO_RELOAD_RESOURCES') || 'Failed to reload resources',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex min-h-[700px] max-h-[88vh] w-[min(98vw,1240px)] max-w-[1240px] flex-col overflow-hidden p-0 sm:min-h-[760px]">
        <DialogHeader className="border-b border-border px-4 py-5 sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FolderTree className="h-5 w-5" />
            {t('BROWSE_INTEGRATION', { name: providerName }) || `Browse ${providerName}`}
          </DialogTitle>
          <DialogDescription>
            {t('BROWSE_PROVIDER_RESOURCES_DESCRIPTION', { name: providerName }) ||
              `Choose the resources to sync from ${providerName}, then press Done to start the sync job.`}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          className="flex min-h-0 flex-1 flex-col"
          onValueChange={(value) => void handleTabChange(value as 'browse' | 'resources' | 'logs')}
          value={activeTab}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6">
            <TabsList className="h-9">
              <TabsTrigger value="browse">{t('BROWSE') || 'Browse'}</TabsTrigger>
              <TabsTrigger value="resources">{t('INDEXED_RESOURCES') || 'Indexed Resources'}</TabsTrigger>
              <TabsTrigger value="logs">{t('SYNC_ACTIVITY') || 'Sync activity'}</TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {t('SELECTED_COUNT', { count: selectedCount }) || `${selectedCount} selected`}
              </Badge>
              <Button
                aria-label={t('REFRESH') || 'Refresh'}
                className="h-8 px-2"
                disabled={isRefreshing || isSubmitting}
                onClick={() => void handleRefreshClick()}
                type="button"
                variant="outline"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          <TabsContent className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-4 sm:px-6" value="browse">
            {selectedNodeList.length > 0 ? (
              <div className="mb-4 rounded-lg border border-border bg-muted/20 px-4 py-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-foreground">{t('SELECTED_FOR_SYNC') || 'Selected for sync'}</p>
                  <Badge variant="outline">
                    {t('SELECTED_COUNT', { count: selectedNodeList.length }) || `${selectedNodeList.length} selected`}
                  </Badge>
                </div>
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  {/* <div className="relative w-full sm:max-w-xs">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      onChange={(event) => setSelectedSearchQuery(event.target.value)}
                      placeholder={t('SEARCH_SELECTED_ITEMS') || 'Search selected items'}
                      value={selectedSearchQuery}
                    />
                  </div> */}
                  <p className="text-xs text-muted-foreground">
                    {t('SHOWING_X_OF_Y', { shown: paginatedSelectedNodes.length, total: filteredSelectedNodes.length }) ||
                      `Showing ${paginatedSelectedNodes.length} of ${filteredSelectedNodes.length}`}
                  </p>
                </div>
                {filteredSelectedNodes.length === 0 ? (
                  <div className="flex min-h-24 items-center justify-center rounded-md border border-dashed border-border bg-background/60 px-4 text-center text-sm text-muted-foreground">
                    {t('NO_SELECTED_ITEMS_MATCH_SEARCH') || 'No selected items match your search.'}
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {paginatedSelectedNodes.map((node) => (
                        <button
                          key={node.source_node_id}
                          className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1 text-xs transition-colors ${focusedSelectedNodeId === node.source_node_id
                            ? 'border-primary bg-primary/5'
                            : 'border-border bg-background hover:border-primary/40 hover:bg-muted/40'
                            }`}
                          onClick={() => {
                            setActiveTab('browse');
                            setFocusedSelectedNodeId(node.source_node_id);

                            if (visibleNodeIds.has(node.source_node_id)) {
                              const nodeIndex = currentNodes.findIndex(
                                (currentNode) => currentNode.source_node_id === node.source_node_id
                              );

                              setBrowseSearchQuery('');
                              setBrowsePage(nodeIndex >= 0 ? Math.floor(nodeIndex / BROWSE_PAGE_SIZE) + 1 : 1);

                              window.requestAnimationFrame(() => {
                                nodeRowRefs.current[node.source_node_id]?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'center',
                                });
                              });
                              return;
                            }

                            toast({
                              variant: 'warning',
                              title: t('SELECTED_ITEM_NOT_VISIBLE') || 'Selected item not visible here',
                              description: t('SELECTED_ITEM_NOT_VISIBLE_DESCRIPTION') ||
                                'This item is selected, but it is not in the currently opened browse level.',
                            });
                          }}
                          type="button"
                        >
                          <span className="truncate font-medium text-foreground">{node.node_title}</span>
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] capitalize text-muted-foreground">
                            {node.node_type}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {visibleNodeIds.has(node.source_node_id)
                              ? t('VISIBLE_HERE') || 'Visible here'
                              : t('BROWSE_TAB') || 'Browse tab'}
                          </span>
                        </button>
                      ))}
                    </div>
                    {selectedTotalPages > 1 ? (
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                        <p className="text-xs text-muted-foreground">
                          {t('PAGE_OF', { page: safeSelectedPage, total: selectedTotalPages }) ||
                            `Page ${safeSelectedPage} of ${selectedTotalPages}`}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            disabled={safeSelectedPage === 1}
                            onClick={() => setSelectedPage((current) => Math.max(1, current - 1))}
                            type="button"
                            variant="outline"
                          >
                            {t('PREVIOUS') || 'Previous'}
                          </Button>
                          <Button
                            disabled={safeSelectedPage >= selectedTotalPages}
                            onClick={() => setSelectedPage((current) => Math.min(selectedTotalPages, current + 1))}
                            type="button"
                            variant="outline"
                          >
                            {t('NEXT') || 'Next'}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-muted/20 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <Button
                  className="h-8 px-2"
                  disabled={(path.length === 0 || isAtGoogleDriveRoot) || isLoadingTree || isSubmitting}
                  onClick={() => void handleGoBack()}
                  type="button"
                  variant="outline"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {path.length === 0 ? t('ROOT_RESOURCES') || 'Root resources' : path[path.length - 1].title}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {path.length === 0
                      ? t('BROWSE_CONNECTED_ACCOUNT', { name: providerName }) ||
                        `Browse the top level of the connected ${providerName} account.`
                      : path.map((node) => node.title).join(' / ')}
                  </p>
                </div>
              </div>
              <div className="relative w-full lg:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  onChange={(event) => {
                    setBrowseSearchQuery(event.target.value);
                    setBrowsePage(1);
                  }}
                  placeholder={t('SEARCH_THIS_LEVEL') || 'Search this level'}
                  value={browseSearchQuery}
                />
              </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 pr-1">
              <p className="text-xs text-muted-foreground">
                {t('SHOWING_RESOURCES_IN_LEVEL', { shown: browseShownCount, total: filteredCurrentNodes.length }) ||
                  `Showing ${browseShownCount} of ${filteredCurrentNodes.length} resources in this level.`}
              </p>
              {browseTotalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-muted-foreground">
                    {t('PAGE_OF', { page: safeBrowsePage, total: browseTotalPages }) ||
                      `Page ${safeBrowsePage} of ${browseTotalPages}`}
                  </p>
                  <Button
                    className="h-8 w-8 px-0"
                    disabled={safeBrowsePage === 1}
                    onClick={() => setBrowsePage((current) => Math.max(1, current - 1))}
                    type="button"
                    variant="outline"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    className="h-8 w-8 px-0"
                    disabled={safeBrowsePage >= browseTotalPages}
                    onClick={() => setBrowsePage((current) => Math.min(browseTotalPages, current + 1))}
                    type="button"
                    variant="outline"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1 sm:pr-2">
                {isLoading ? (
                  <div className="flex h-full min-h-56 items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('LOADING_PROVIDER_RESOURCES', { name: providerName }) || `Loading ${providerName} resources...`}</span>
                  </div>
                ) : currentNodes.length === 0 ? (
                  <div className="flex h-full min-h-56 items-center justify-center text-sm text-muted-foreground">
                    {t('NO_RESOURCES_FOUND_FOR_LEVEL') || 'No resources found for this level.'}
                  </div>
                ) : filteredCurrentNodes.length === 0 ? (
                  <div className="flex h-full min-h-56 items-center justify-center px-4 text-center text-sm text-muted-foreground">
                    {t('NO_RESOURCES_MATCH_SEARCH_IN_LEVEL') || 'No resources match your search in this level.'}
                  </div>
                ) : (
                  <div className="space-y-2 pb-6">
                    {paginatedCurrentNodes.map((node) => {
                      const isChecked = Boolean(selectedNodes[node.source_node_id]);
                      const canOpenChildren = node.has_children;

                      return (
                        <div
                          key={node.source_node_id}
                          ref={(element) => {
                            nodeRowRefs.current[node.source_node_id] = element;
                          }}
                          className={`flex items-center gap-4 rounded-lg border border-border bg-card px-5 py-3 ${focusedSelectedNodeId === node.source_node_id
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                            : ''
                            } ${canOpenChildren ? 'cursor-pointer hover:border-primary/40 hover:bg-muted/40' : ''
                            }`}
                          onClick={() => {
                            if (canOpenChildren && !isLoadingTree && !isSubmitting) {
                              void handleOpenNode(node);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (!canOpenChildren || isLoadingTree || isSubmitting) {
                              return;
                            }

                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              void handleOpenNode(node);
                            }
                          }}
                          role={canOpenChildren ? 'button' : undefined}
                          tabIndex={canOpenChildren ? 0 : undefined}
                        >
                          <Checkbox
                            checked={isChecked}
                            disabled={isSubmitting}
                            onCheckedChange={(checked) => handleToggleSelection(node, checked === true)}
                            onClick={(event) => event.stopPropagation()}
                          />

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 pr-3">
                              <p className="text-sm font-medium text-foreground">{node.title}</p>
                              <Badge className="capitalize" variant="outline">
                                {node.node_type}
                              </Badge>
                              {typeof node.item_count === 'number' ? (
                                <span className="text-xs text-muted-foreground">
                                  {t('ITEMS_COUNT', { count: node.item_count }) || `${node.item_count} items`}
                                </span>
                              ) : null}
                            </div>
                            {node.description ? (
                              <p className="text-xs text-muted-foreground">{node.description}</p>
                            ) : null}
                          </div>

                          {node.has_children ? (
                            <Button
                              className="h-8 shrink-0 px-2"
                              disabled={isLoadingTree || isSubmitting}
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleOpenNode(node);
                              }}
                              type="button"
                              variant="ghost"
                            >
                              <span className="text-xs font-medium">{t('OPEN') || 'Open'}</span>
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          </TabsContent>

          <TabsContent className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-4 sm:px-6" value="resources">
            <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-muted/20 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{t('INDEXED_RESOURCES') || 'Indexed resources'}</p>
                <p className="text-xs text-muted-foreground">
                  {t('INDEXED_RESOURCES_DESCRIPTION') ||
                    'These files are already available in the searchable index for this connection.'}
                </p>
              </div>

              <div className="flex w-full flex-col gap-2 lg:max-w-xl">
                <div className="relative w-full">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    onChange={(event) => setIndexedResourcesSearchQuery(event.target.value)}
                    placeholder={t('SEARCH_INDEXED_FILES_BY_NAME') || 'Search indexed files by name'}
                    value={indexedResourcesSearchQuery}
                  />
                </div>
                {indexedResourcesSearchHintVisible ? (
                  <p className="text-xs text-muted-foreground">
                    {t('TYPE_AT_LEAST_TWO_CHARACTERS') || 'Type at least 2 characters to filter by resource name.'}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 pr-1">
              <p className="text-xs text-muted-foreground">
                {indexedResourcesTotal === 0
                  ? t('NO_INDEXED_RESOURCES_FOUND') || 'No indexed resources found.'
                  : t('SHOWING_SYNCED_RESOURCES', {
                      from: indexedResourcesShownFrom,
                      to: indexedResourcesShownTo,
                      total: indexedResourcesTotal,
                    }) ||
                    `Showing ${indexedResourcesShownFrom}-${indexedResourcesShownTo} of ${indexedResourcesTotal} successfully synced resources.`}
              </p>
              {indexedResourcesTotalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-muted-foreground">
                    {t('PAGE_OF', { page: indexedResourcesPage, total: indexedResourcesTotalPages }) ||
                      `Page ${indexedResourcesPage} of ${indexedResourcesTotalPages}`}
                  </p>
                  <Button
                    className="h-8 w-8 px-0"
                    disabled={indexedResourcesOffset === 0 || isLoadingIndexedResources}
                    onClick={() => void loadIndexedResources(
                      Math.max(0, indexedResourcesOffset - INDEXED_RESOURCES_PAGE_SIZE),
                      indexedResourcesSubmittedQuery
                    )}
                    type="button"
                    variant="outline"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    className="h-8 w-8 px-0"
                    disabled={
                      isLoadingIndexedResources ||
                      indexedResourcesOffset + INDEXED_RESOURCES_PAGE_SIZE >= indexedResourcesTotal
                    }
                    onClick={() => void loadIndexedResources(
                      indexedResourcesOffset + INDEXED_RESOURCES_PAGE_SIZE,
                      indexedResourcesSubmittedQuery
                    )}
                    type="button"
                    variant="outline"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="min-h-[46vh] flex-1 overflow-y-auto pr-1 sm:pr-2">
              {isLoadingIndexedResources ? (
                <div className="flex h-full min-h-56 items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t('LOADING_INDEXED_RESOURCES') || 'Loading indexed resources...'}</span>
                </div>
              ) : indexedResources.length === 0 ? (
                <div className="flex h-full min-h-56 items-center justify-center px-4 text-center text-sm text-muted-foreground">
                  {indexedResourcesSubmittedQuery
                    ? t('NO_INDEXED_RESOURCES_MATCH_SEARCH') || 'No indexed resources match your search.'
                    : t('NO_INDEXED_RESOURCES_AVAILABLE') ||
                      'No indexed resources are available for this connection yet.'}
                </div>
              ) : (
                <div className="space-y-2 pb-6">
                  {indexedResources.map((resource) => (
                    <div
                      key={resource.id}
                      className="rounded-lg border border-border bg-card px-5 py-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-sm font-medium text-foreground">{resource.name}</p>
                            <Badge className="capitalize" variant="outline">
                              {resource.resource_type}
                            </Badge>
                            <Badge
                              className={getSelectionStateBadgeClassName(resource.selection_state)}
                              variant="outline"
                            >
                              {resource.selection_state
                                ? formatSelectionState(resource.selection_state)
                                : t('UNKNOWN') || 'Unknown'}
                            </Badge>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t('EXTERNAL_ID_LABEL', { id: resource.external_id }) || `External ID: ${resource.external_id}`}
                          </p>
                        </div>

                        {resource.uri ? (
                          <a
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                            href={resource.uri}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {t('OPEN_SOURCE') || 'Open source'}
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : null}
                      </div>

                      <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('UPDATED_IN_SOURCE') || 'Updated in source'}</p>
                          <p className="text-foreground">{formatTimestamp(resource.updated_at_external, notAvailableLabel)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('NODE_TYPE') || 'Node type'}</p>
                          <p className="text-foreground">{resource.resource_ref?.node_type || notAvailableLabel}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('NODE_ID') || 'Node ID'}</p>
                          <p className="break-all text-foreground">
                            {resource.resource_ref?.node_id || notAvailableLabel}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent className="mt-0 min-h-0 flex-1 overflow-hidden px-4 py-4 sm:px-6" value="logs">
            <div className="grid gap-4 lg:grid-cols-[440px_minmax(0,1fr)]">
              <div className="rounded-lg border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">{t('SYNC_JOBS') || 'Sync jobs'}</p>
                  </div>
                  <Badge variant="outline">{syncJobs.length}</Badge>
                </div>

                <div className="h-[46vh] overflow-y-auto">
                  {isLoadingSyncJobs ? (
                    <div className="flex h-full min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{t('LOADING_SYNC_JOBS') || 'Loading sync jobs...'}</span>
                    </div>
                  ) : syncJobs.length === 0 ? (
                    <div className="flex h-full min-h-40 items-center justify-center px-4 text-center text-sm text-muted-foreground">
                      {t('NO_SYNC_JOBS_YET') || 'No sync jobs yet for this connection.'}
                    </div>
                  ) : (
                    <div className="space-y-2 p-3 pr-3">
                      {syncJobs.map((job) => (
                        <button
                          key={job.id}
                          className={`w-full rounded-lg border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${selectedSyncJobId === job.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border bg-background hover:border-primary/30 hover:bg-muted/40'
                            }`}
                          onClick={() => void loadSyncJobDetail(job.id)}
                          type="button"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="truncate pr-1 text-sm font-medium text-foreground">{job.id}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatTimestamp(job.started_at, notAvailableLabel)}
                              </p>
                            </div>
                            <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ${getSyncJobStatusTone(job.status)}`}>
                              {job.status || t('UNKNOWN') || 'unknown'}
                            </span>
                          </div>
                          <p className="mt-2 text-xs text-muted-foreground">
                            {job.mode} • {job.trigger}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card">
                <div className="border-b border-border px-4 py-3">
                  <p className="text-sm font-medium text-foreground">{t('JOB_DETAILS') || 'Job details'}</p>
                </div>

                <ScrollArea className="h-[46vh]">
                  {isLoadingSyncJobDetail ? (
                    <div className="flex h-full min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{t('LOADING_SYNC_JOB_DETAILS') || 'Loading sync job details...'}</span>
                    </div>
                  ) : !selectedSyncJob ? (
                    <div className="flex h-full min-h-40 items-center justify-center px-4 text-center text-sm text-muted-foreground">
                      {t('SELECT_SYNC_JOB_TO_VIEW') || 'Select a sync job to view its full status and stats.'}
                    </div>
                  ) : (
                    <div className="space-y-4 p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold text-foreground">{selectedSyncJob.id}</p>
                        <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${getSyncJobStatusTone(selectedSyncJob.status)}`}>
                          {selectedSyncJob.status || t('UNKNOWN') || 'unknown'}
                        </span>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('STARTED') || 'Started'}</p>
                          <p className="text-sm text-foreground">{formatTimestamp(selectedSyncJob.started_at, notAvailableLabel)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('FINISHED') || 'Finished'}</p>
                          <p className="text-sm text-foreground">{formatTimestamp(selectedSyncJob.finished_at, notAvailableLabel)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('MODE') || 'Mode'}</p>
                          <p className="text-sm text-foreground">{selectedSyncJob.mode || notAvailableLabel}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('TRIGGER') || 'Trigger'}</p>
                          <p className="text-sm text-foreground">{selectedSyncJob.trigger || notAvailableLabel}</p>
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">{t('STATS') || 'Stats'}</p>
                        {syncJobStats.length > 0 ? (
                          <div className="grid gap-2 sm:grid-cols-2">
                            {syncJobStats.map(([key, value]) => (
                              <div key={key} className="rounded-md border border-border bg-background px-3 py-2">
                                <p className="text-xs text-muted-foreground">{key}</p>
                                <p className="text-sm font-medium text-foreground">{String(value)}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">{t('NO_STATS_AVAILABLE') || 'No stats available for this job yet.'}</p>
                        )}
                      </div>

                      <Separator />

                      <div>
                        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">{t('ERROR') || 'Error'}</p>
                        <p className="whitespace-pre-wrap text-sm text-foreground">
                          {selectedSyncJob.error ? JSON.stringify(selectedSyncJob.error, null, 2) : t('NO_ERROR_REPORTED') || 'No error reported.'}
                        </p>
                      </div>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="shrink-0 border-t border-border px-4 py-4 sm:px-6">
          <div className="flex w-full items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {selectedCount > 0
                ? t('DONE_WILL_SAVE_SELECTIONS', { count: selectedCount }) ||
                  `Done will save ${selectedCount} selection${selectedCount === 1 ? '' : 's'} and start an incremental sync job.`
                : t('DONE_WILL_CLEAR_SELECTIONS') ||
                  'Done will clear saved selections and start an incremental sync for the full connection.'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
                type="button"
                variant="outline"
              >
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button
                disabled={isLoading || isSubmitting}
                onClick={() => void handleDone()}
                type="button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('STARTING_SYNC') || 'Starting sync...'}
                  </>
                ) : (
                  t('DONE') || 'Done'
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
